import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserName, useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface NMXA105C06FormData {
  // Información general
  noSolicitud: string
  numeroSolicitud: string
  fecha: string
  diacetato_dw_label?: string
  diacetato_tv_label?: string
  metodo: string
  metodoCampo: string
  cloroDisponible: string
  cloroCampo: string
  tiempo: string
  tiempoCampo: string
  tempSecado: string
  tempSecadoCampo: string
  volBano: string
  volBanoCampo: string
  temperatura: string
  temperaturaCampo: string
  balines: string
  balinesCampo: string
  ajustePh: string
  ajustePhCampo: string
  tipoSecado: string
  tipoSecadoCampo: string
  detergente: string
  detergenteCampo: string
  realizo: string
  
  // --- NUEVOS CAMPOS ---
  DESV_METODO?: string;
  ACIDULACION?: string;
  tipoMultifibra: string;

  // TELA MULTIFIBRA DW
  diacetato_dw: string
  algodon_blanqueado_dw: string
  poliamida_dw: string
  poliester_dw: string
  acrilico_dw: string
  lana_dw: string
  cambio_color_dw: string
  
  // TELA MULTIFIBRA TV
  diacetato_tv: string
  algodon_blanqueado_tv: string
  poliamida_tv: string
  poliester_tv: string
  acrilico_tv: string
  viscosa_tv: string
  cambio_color_tv: string
  
  // Supervisión y observaciones
  superviso: string
  observaciones: string
  mostrarObservaciones: boolean
}

interface NMXA105C06FormProps {
  mode: "create" | "view" | "edit"
  initialData?: Partial<NMXA105C06FormData>
  onSave?: (data: NMXA105C06FormData) => void
  onCancel?: () => void
  realizoUser?: string // <-- NUEVO: usuario que realizó la prueba
  onRefreshBackend?: () => void
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA105C06Form({
  mode,
  initialData,
  onSave,
  onCancel,
  realizoUser, // <-- NUEVO
  onRefreshBackend,
  pedidoRowId,
  onAutosave,
}: NMXA105C06FormProps) {
  const globalRol = useUserRol();
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const userName = useUserName();
  const effectiveRol = globalRol;

  // --- Inicialización robusta de realizo ---
  const [formData, setFormData] = useState<NMXA105C06FormData>({
    ...initialData,
    realizo: (mode === 'create' && realizoUser) ? realizoUser : (initialData?.realizo || ''),
    noSolicitud: "",
    numeroSolicitud: "",
    fecha: "",
    metodo: "",
    metodoCampo: "",
    cloroDisponible: "",
    cloroCampo: "",
    tiempo: "",
    tiempoCampo: "",
    tempSecado: "",
    tempSecadoCampo: "",
    volBano: "",
    volBanoCampo: "",
    temperatura: "",
    temperaturaCampo: "",
    balines: "",
    balinesCampo: "",
    ajustePh: "",
    ajustePhCampo: "",
    tipoSecado: "",
    tipoSecadoCampo: "",
    detergente: "",
    detergenteCampo: "",
    DESV_METODO: (initialData as any)?.DESV_METODO || (initialData as any)?.desv_metodo || '',
    ACIDULACION: (initialData as any)?.ACIDULACION || (initialData as any)?.acidulacion || '',
    tipoMultifibra: initialData?.tipoMultifibra || "DW",
    diacetato_dw: "",
    algodon_blanqueado_dw: "",
    poliamida_dw: "",
    poliester_dw: "",
    acrilico_dw: "",
    lana_dw: "",
    cambio_color_dw: "",
    diacetato_tv: "",
    algodon_blanqueado_tv: "",
    poliamida_tv: "",
    poliester_tv: "",
    acrilico_tv: "",
    viscosa_tv: "",
    cambio_color_tv: "",
    superviso: "",
    observaciones: "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '');
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData(prev => {
      const merged = { ...prev, ...initialData };
      merged.DESV_METODO = (initialData as any)?.DESV_METODO || (initialData as any)?.desv_metodo || prev.DESV_METODO || '';
      merged.ACIDULACION = (initialData as any)?.ACIDULACION || (initialData as any)?.acidulacion || prev.ACIDULACION || '';
      merged.mostrarObservaciones = (initialData as any)?.MOSTRAR_OBSERVACIONES === true;
      return merged;
    });
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  // Lógica robusta para saber si la prueba está aceptada o rechazada
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';

  const isReadOnly = mode === "view" && !editMode

  const handleInputChange = (field: keyof NMXA105C06FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) {
        onSave(formData)
      }
    } finally {
      setGuardando(false)
    }
  }

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData as any
    return rest
  }

  // --- ADMIN ACTIONS robustas igual que AnalisisQuimicoForm ---
  // RowId robusto: primero formData, luego initialData, luego vacío
  const [autosaveRowId, setAutosaveRowId] = useState<string>((formData as any)?.ROW_ID || (initialData as any)?.ROW_ID || (initialData as any)?.row_id || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId
  const pedidoValue = (formData as any).PEDIDO || (initialData as any)?.PEDIDO || '';
  const pruebaValue = (formData as any).PRUEBA || (initialData as any)?.PRUEBA || '';

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-105-C06-INNTEX-2015 SOLIDEZ DEL COLOR AL LAVADO DOMESTICO E INDUSTRIAL',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleAccion = async (
    accion: 'aceptado' | 'rechazado',
    razonRechazo?: string
  ) => {
    if (accion === 'rechazado' && !razonRechazo) {
      setShowRechazoModal(true);
      return;
    }
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-105-C06?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-105-C06?';
    if (!window.confirm(confirmMsg)) return;
    setRechazoLoading(true);
    const payload = {
      ...formData,
      ROW_ID: rowId, // <-- siempre manda el rowId correcto
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      ...(accion === 'rechazado' && razonRechazo ? { RAZON_RECHAZO: razonRechazo } : {})
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? (payload as any).RAZON_RECHAZO : undefined, (payload as any).superviso, nomId);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
      setShowRechazoModal(false);
    }
  };

  const handleEditar = () => setEditMode(true);
  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-105-C06? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case "create":
        return "Crear NMX-A-105-C06-INNTEX-2015"
      case "edit":
        return "Editar NMX-A-105-C06-INNTEX-2015"
      case "view":
        return "Ver NMX-A-105-C06-INNTEX-2015"
      default:
        return "NMX-A-105-C06-INNTEX-2015"
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackFlush}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1>{getTitle()}</h1>
        </div>
        <div className="flex gap-2">
          {/* Admin action buttons */}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={handleEditar}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {/* Botón guardar en modo create */}
          {mode === 'create' && (
            <Button onClick={handleSave} className="gap-2" disabled={guardando}>
              <Save className="h-4 w-4" />
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-center text-lg">
            NMX-A-105-C06-INNTEX-2015 SOLIDEZ DEL COLOR AL LAVADO DOMESTICO E INDUSTRIAL
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Información General */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <tbody>
                {/* Primera fila - NO. DE SOLICITUD y FECHA */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    NO. DE SOLICITUD:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Número de solicitud"
                      value={formData.numeroSolicitud}
                      onChange={(e) => handleInputChange('numeroSolicitud', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    FECHA:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleInputChange('fecha', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                </tr>

                {/* Segunda fila - MÉTODO, CLORO DISPONIBLE, TIEMPO, TEMP. DE SECADO */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    MÉTODO:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Método"
                      value={formData.metodoCampo}
                      onChange={(e) => handleInputChange('metodoCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    CLORO DISPONIBLE:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Cloro disponible"
                      value={formData.cloroCampo}
                      onChange={(e) => handleInputChange('cloroCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    TIEMPO:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Tiempo"
                      value={formData.tiempoCampo}
                      onChange={(e) => handleInputChange('tiempoCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    TEMP. DE SECADO:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Temperatura secado"
                      value={formData.tempSecadoCampo}
                      onChange={(e) => handleInputChange('tempSecadoCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                </tr>

                {/* Tercera fila - VOL. BAÑO, TEMPERATURA, BALINES, AJUSTE PH */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    VOL. BAÑO:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Volumen baño"
                      value={formData.volBanoCampo}
                      onChange={(e) => handleInputChange('volBanoCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    TEMPERATURA:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Temperatura"
                      value={formData.temperaturaCampo}
                      onChange={(e) => handleInputChange('temperaturaCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    BALINES:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Balines"
                      value={formData.balinesCampo}
                      onChange={(e) => handleInputChange('balinesCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    AJUSTE PH:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="text"
                      placeholder="Ajuste pH"
                      value={formData.ajustePhCampo}
                      onChange={(e) => handleInputChange('ajustePhCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                </tr>

                {/* Cuarta fila - TIPO DE SECADO y DETERGENTE */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    TIPO DE SECADO:
                  </td>
                  <td className="border border-border p-1" colSpan={3}>
                    <Input
                      type="text"
                      placeholder="Tipo de secado"
                      value={formData.tipoSecadoCampo}
                      onChange={(e) => handleInputChange('tipoSecadoCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    DETERGENTE:
                  </td>
                  <td className="border border-border p-1" colSpan={3}>
                    <Input
                      type="text"
                      placeholder="Detergente"
                      value={formData.detergenteCampo}
                      onChange={(e) => handleInputChange('detergenteCampo', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-primary">Parámetros adicionales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="desvMetodo" className="block text-sm font-medium text-gray-700">Desviación al método</label>
                  <Input
                    id="desvMetodo"
                    type="text"
                    placeholder="Ingrese la desviación al método"
                    value={formData.DESV_METODO || ""}
                    onChange={e => handleInputChange('DESV_METODO', e.target.value)}
                    disabled={isReadOnly}
                    className="mt-2"
                  />
                </div>
                <div>
                  <label htmlFor="acidulacion" className="block text-sm font-medium text-gray-700">Tratamiento de acidulación</label>
                  <Input
                    id="acidulacion"
                    type="text"
                    placeholder="Ingrese el tratamiento de acidulación"
                    value={formData.ACIDULACION || ""}
                    onChange={e => handleInputChange('ACIDULACION', e.target.value)}
                    disabled={isReadOnly}
                    className="mt-2"
                  />
                </div>
                <div>
                  <label htmlFor="tipoMultifibra" className="block text-sm font-medium text-gray-700">Tipo de Multifibra</label>
                  <Select
                    value={formData.tipoMultifibra}
                    onValueChange={value => handleInputChange('tipoMultifibra', value)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Seleccione tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DW">DW</SelectItem>
                      <SelectItem value="TV">TV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tablas de Resultados */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* TELA MULTIFIBRA DW */}
            <div className="lg:col-span-1">
              <table className="w-full border-collapse border border-border">
                <tbody>
                  <tr>
                    <td className="border border-border p-3 bg-muted text-center font-medium" colSpan={2}>
                      TELA MULTIFIBRA DW
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium text-center">
                      FIBRA
                    </td>
                    <td className="border border-border p-2 bg-muted font-medium text-center">
                      TRANSFERENCIA
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-1 bg-muted">
                      <Input
                        type="text"
                        value={formData.diacetato_dw_label ?? 'diacetato (acetato)'}
                        onChange={(e) => handleInputChange('diacetato_dw_label', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent font-medium"
                      />
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.diacetato_dw}
                        onChange={(e) => handleInputChange('diacetato_dw', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      ALGODÓN blanqueado
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.algodon_blanqueado_dw}
                        onChange={(e) => handleInputChange('algodon_blanqueado_dw', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      poliamida
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.poliamida_dw}
                        onChange={(e) => handleInputChange('poliamida_dw', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      poliéster
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.poliester_dw}
                        onChange={(e) => handleInputChange('poliester_dw', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      acrílico
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.acrilico_dw}
                        onChange={(e) => handleInputChange('acrilico_dw', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      lana
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.lana_dw}
                        onChange={(e) => handleInputChange('lana_dw', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      CAMBIO DE COLOR
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.cambio_color_dw}
                        onChange={(e) => handleInputChange('cambio_color_dw', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* TELA MULTIFIBRA TV */}
            <div className="lg:col-span-1">
              <table className="w-full border-collapse border border-border">
                <tbody>
                  <tr>
                    <td className="border border-border p-3 bg-muted text-center font-medium" colSpan={2}>
                      TELA MULTIFIBRA TV
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium text-center">
                      FIBRA
                    </td>
                    <td className="border border-border p-2 bg-muted font-medium text-center">
                      TRANSFERENCIA
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-1 bg-muted">
                      <Input
                        type="text"
                        value={formData.diacetato_tv_label ?? 'triacetato (acetato)'}
                        onChange={(e) => handleInputChange('diacetato_tv_label', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent font-medium"
                      />
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.diacetato_tv}
                        onChange={(e) => handleInputChange('diacetato_tv', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      ALGODÓN blanqueado
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.algodon_blanqueado_tv}
                        onChange={(e) => handleInputChange('algodon_blanqueado_tv', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      poliamida
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.poliamida_tv}
                        onChange={(e) => handleInputChange('poliamida_tv', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      poliéster
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.poliester_tv}
                        onChange={(e) => handleInputChange('poliester_tv', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      acrílico
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.acrilico_tv}
                        onChange={(e) => handleInputChange('acrilico_tv', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      viscosa
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.viscosa_tv}
                        onChange={(e) => handleInputChange('viscosa_tv', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 bg-muted font-medium">
                      CAMBIO DE COLOR
                    </td>
                    <td className="border border-border p-1">
                      <Input
                        type="text"
                        placeholder="Valor"
                        value={formData.cambio_color_tv}
                        onChange={(e) => handleInputChange('cambio_color_tv', e.target.value)}
                        disabled={isReadOnly}
                        className="border-0 bg-transparent text-center"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* REALIZÓ y SUPERVISÓ */}
            <div className="lg:col-span-1 space-y-4">
              <div className="border border-border p-4 bg-muted">
                <div className="font-medium mb-2">REALIZÓ:</div>
                <Input
                  type="text"
                  placeholder="Nombre del responsable"
                  value={formData.realizo}
                  onChange={(e) => handleInputChange('realizo', e.target.value)}
                  disabled
                  className="bg-background"
                />
              </div>
              
              <div className="border border-border p-4 bg-muted">
                <div className="font-medium mb-2">SUPERVISÓ:</div>
                <Input
                  type="text"
                  placeholder="Supervisor"
                  value={formData.superviso}
                  onChange={(e) => handleInputChange('superviso', e.target.value)}
                  disabled
                  className="bg-background"
                />
              </div>
            </div>
          </div>

          {/* Observaciones */}
          <div className="space-y-2">
            <div className="border border-border p-2 bg-muted font-medium flex items-center gap-2">
              OBSERVACIONES:
              <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                Mostrar en Reporte Cliente
                <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                  onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
              </span>
            </div>
            <Textarea
              placeholder="Observaciones adicionales sobre la prueba..."
              value={formData.observaciones}
              onChange={(e) => handleInputChange('observaciones', e.target.value)}
              disabled={isReadOnly}
              className="min-h-[100px]"
            />
          </div>
        </CardContent>
      </Card>
      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}